/**
 * FormConcierge-Mirror — mirrors each Form Concierge session (a plansheet Request + its turns) into Headlight as one
 * Document of the `FormConcierge-Session` form type, so sessions are durable and browsable on the Headlight side too.
 *
 * Layout (all in the session customer, which for v1 is always customer 1 — super-users only):
 *   - form type: the RNI Hash `FormConcierge-Session` (seeded by seed-form-concierge-config.js); missing → mirror OFF.
 *   - project:   `GUIDProject = FormConcierge-<IDCustomer>-Sessions`, joined to the RNI (ReportNamedInstanceProjectJoin)
 *                so its documents render with this form rather than the project's first one.
 *   - document:  `GUIDDocument = FormConcierge-Session-<GUIDRequest>`, FormData as an OBJECT (never a string).
 *
 * Every deterministic-GUID find-or-create walks the suffixes '', '-2', '-3': a live hit is used; a GUID held only by a
 * soft-deleted row is skipped (Meadow's create sees deleted rows and refuses the GUID forever); a free GUID is created
 * and then read back, because some Meadow writes answer 200 `{}` when they were refused.
 *
 * Everything here is fire-and-forget for the screen: no public method rejects; failures come back as
 * `{ Skipped:true, Reason }` for a quiet inline warning. All plansheet-sourced strings are stored as data only (the
 * form's descriptors are String/TextArea/Number/DateTime, D12) and never interpolated into a Meadow filter unchecked.
 *
 * No window/document access anywhere; the REST client is injected (HeadlightRestClient-like, URLs WITHOUT /1.0/).
 */

const RNI_HASH = 'FormConcierge-Session';
const DOCUMENT_TYPE = 'FormConcierge-Session';
const DOCUMENT_GUID_PREFIX = 'FormConcierge-Session-';
const PROJECT_NAME = 'Form Concierge — Sessions';
const PROJECT_STATUS = 'FormConcierge';
const PROJECT_DESCRIPTION = 'Headlight mirror of Form Concierge sessions (the plansheet form pipeline): one Document per session. Maintained by the Platform Configuration app.';
const GUID_SUFFIXES = [ '', '-2', '-3' ];
// Plansheet GUIDs are UUIDs; anything else is refused before it can reach a Meadow filter expression.
const GUID_PATTERN = /^[0-9A-Za-z-]{1,200}$/;
const NOT_INSTALLED_REASON = 'Not mirrored to Headlight: the FormConcierge-Session form type isn\'t installed in this environment';
const READY_RETRY_MS = 60000;
const RECONCILE_PROBE_CAP = 100;
const RECONCILE_SYNC_CAP = 5;
const DOCUMENT_NAME_MAX = 128;
const TURN_CONTENT_MAX = 200000;
const TRANSCRIPT_MAX = 600000;
const EXAMPLES_MAX = 25;
const FLOW_LABELS = { create: 'Create', update: 'Update', build: 'Build', examples: 'Examples' };

// Steering sentinels (C1). The façade already strips them into Display.UserText; these only back the fallback below.
const STEERING_MARKER = '\n\n---\nForm Concierge steering:\n';
const EXAMPLES_PREFIX = '[examples] ';
const REVISION_PREFIX = '[revision] ';
const STANDING_HEADING = '## Standing instructions';

/**
 * C6 stripping: drop a leading [examples]/[revision] paragraph and everything from the steering marker on.
 * @param {any} pText - Raw turn text.
 * @return {string}
 */
const stripSteering = (pText) =>
{
	let tmpText = (typeof pText === 'string') ? pText : '';
	const tmpMarker = tmpText.indexOf(STEERING_MARKER);
	if (tmpMarker >= 0)
	{
		tmpText = tmpText.slice(0, tmpMarker);
	}
	tmpText = tmpText.replace(/^\s+/, '');
	if ((tmpText.indexOf(EXAMPLES_PREFIX) === 0) || (tmpText.indexOf(REVISION_PREFIX) === 0))
	{
		const tmpBreak = tmpText.indexOf('\n\n');
		tmpText = (tmpBreak >= 0) ? tmpText.slice(tmpBreak + 2) : '';
	}
	return tmpText.trim();
};

/**
 * The user's own segment of an assembled first turn ('## Standing instructions … ## <Input> …'); '' when there is
 * none, so the standing instructions never land in the mirror.
 * @param {string} pContent - The first user turn's Content.
 * @return {string}
 */
const userSegmentOfAssembledTurn = (pContent) =>
{
	if (pContent.indexOf(STANDING_HEADING) !== 0)
	{
		return pContent;
	}
	const tmpHeading = /\n## [^\n]*\n/g;
	tmpHeading.lastIndex = STANDING_HEADING.length;
	const tmpMatch = tmpHeading.exec(pContent);
	return tmpMatch ? pContent.slice(tmpMatch.index + tmpMatch[0].length).replace(/^\n+/, '') : '';
};

/**
 * A single-line, control-free string cut to a width (names, hashes, roles).
 * @param {any} pValue - Anything.
 * @param {number} pMax - Maximum length.
 * @return {string}
 */
const cleanLine = (pValue, pMax) =>
{
	if ((pValue === undefined) || (pValue === null))
	{
		return '';
	}
	// eslint-disable-next-line no-control-regex
	return String(pValue).replace(/[\x00-\x1f\x7f]+/g, ' ').trim().slice(0, pMax);
};

/**
 * Multi-line text cut to a width, with a visible marker when cut.
 * @param {any} pValue - Anything.
 * @param {number} pMax - Maximum length.
 * @return {string}
 */
const cleanText = (pValue, pMax) =>
{
	if (typeof pValue !== 'string')
	{
		return '';
	}
	// eslint-disable-next-line no-control-regex
	const tmpText = pValue.replace(/\x00/g, '');
	return (tmpText.length > pMax) ? `${tmpText.slice(0, pMax)}\n\n…[truncated]` : tmpText;
};

/**
 * @param {any} pValue
 * @return {boolean}
 */
const isPlainObject = (pValue) => !!pValue && (typeof pValue === 'object') && !Array.isArray(pValue);

/**
 * Deep copy with every '<' in every string replaced by a look-alike (U+2039). The mirror form's PDF/Print render
 * writes String and TextArea values with innerHTML, so nothing we store may be able to become markup there.
 * @param {any} pValue - Anything JSON-shaped.
 * @return {any}
 */
const neutralizeMarkup = (pValue) =>
{
	if (typeof pValue === 'string')
	{
		return pValue.replace(/</g, '\u2039');
	}
	if (Array.isArray(pValue))
	{
		return pValue.map(neutralizeMarkup);
	}
	if (isPlainObject(pValue))
	{
		const tmpOut = {};
		Object.keys(pValue).forEach((pKey) =>
		{
			tmpOut[pKey] = neutralizeMarkup(pValue[pKey]);
		});
		return tmpOut;
	}
	return pValue;
};

/**
 * Message metadata as an object (the façade parses it; a string is tolerated and parsed here). Never a string.
 * @param {any} pValue - Metadata / MetadataJSON.
 * @return {Record<string, any>|null}
 */
const parseMetadata = (pValue) =>
{
	let tmpValue = pValue;
	if (typeof tmpValue === 'string')
	{
		if (tmpValue.trim().length < 1)
		{
			return null;
		}
		try
		{
			tmpValue = JSON.parse(tmpValue);
		}
		catch (pError)
		{
			return null;
		}
	}
	if (!isPlainObject(tmpValue))
	{
		return null;
	}
	try
	{
		return JSON.parse(JSON.stringify(tmpValue));
	}
	catch (pError)
	{
		return null;
	}
};

/**
 * Milliseconds for a date-ish value, or 0. Accepts ISO strings and MySQL-style 'YYYY-MM-DD HH:mm:ss' (read as UTC).
 * @param {any} pValue
 * @return {number}
 */
const toMillis = (pValue) =>
{
	if (!pValue)
	{
		return 0;
	}
	let tmpParsed = Date.parse(String(pValue));
	if (!isFinite(tmpParsed) && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(String(pValue)))
	{
		tmpParsed = Date.parse(`${String(pValue).replace(' ', 'T')}Z`);
	}
	return isFinite(tmpParsed) ? tmpParsed : 0;
};

/**
 * 'YYYY-MM-DD HH:mm UTC' for the transcript, or the raw value when it doesn't parse.
 * @param {any} pValue
 * @return {string}
 */
const transcriptDate = (pValue) =>
{
	const tmpMillis = toMillis(pValue);
	if (!tmpMillis)
	{
		return pValue ? cleanLine(pValue, 40) : '-';
	}
	return `${new Date(tmpMillis).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

/**
 * The session's messages, sorted by Sequence (the façade sends them ascending; this does not rely on it).
 * @param {any} pPayload - GET Session payload.
 * @return {Array<any>}
 */
const sortedMessages = (pPayload) =>
{
	const tmpMessages = (pPayload && Array.isArray(pPayload.Messages)) ? pPayload.Messages.filter(isPlainObject) : [];
	return tmpMessages.slice().sort((pA, pB) => (Number(pA.Sequence) || 0) - (Number(pB.Sequence) || 0));
};

/**
 * The status to mirror: Request.Status, except an active run the façade flagged as a zombie reads as the Sessions
 * list shows it (B14) — 'NotPickedUp', or 'Cancelled' when a cancel was requested.
 * @param {any} pPayload - GET Session payload.
 * @return {string}
 */
const effectiveStatus = (pPayload) =>
{
	const tmpSession = (pPayload && isPlainObject(pPayload.Session)) ? pPayload.Session : {};
	const tmpStatus = cleanLine(tmpSession.Status, 32);
	const tmpRun = (pPayload && isPlainObject(pPayload.Run)) ? pPayload.Run : null;
	if (((tmpStatus === 'Pending') || (tmpStatus === 'Running')) && tmpRun && (tmpRun.Zombie === true))
	{
		return (tmpRun.CancelRequested === true) ? 'Cancelled' : 'NotPickedUp';
	}
	return tmpStatus;
};

/**
 * An example-document render URL (D2 exception): https, no credentials, `/1.0/Report/<guid>/index.html`, no query/hash.
 * @param {any} pValue
 * @return {string} The normalized URL, or ''.
 */
const reportURL = (pValue) =>
{
	if ((typeof pValue !== 'string') || (pValue.length > 600))
	{
		return '';
	}
	let tmpURL;
	try
	{
		tmpURL = new URL(pValue);
	}
	catch (pError)
	{
		return '';
	}
	if ((tmpURL.protocol !== 'https:') || tmpURL.username || tmpURL.password || tmpURL.search || tmpURL.hash)
	{
		return '';
	}
	return /^\/1\.0\/Report\/[0-9A-Za-z-]+\/index\.html$/.test(tmpURL.pathname) ? tmpURL.href : '';
};

// Document-id cues near a render URL, most specific first.
const EXAMPLE_ID_PATTERNS = [
	/\bIDDocument\D{0,4}?(\d{1,10})\b/i,
	/\bdoc(?:ument)?\s*(?:id\s*)?[#:]?\s*(\d{3,10})\b/i,
	/(?:^|[^\w$.,/:-])(\d{5,10})(?!\d|[.,]\d)/,
];

/**
 * Find a document id (and the label that follows it) in a line of text.
 * @param {string} pText - The text before the URL on its line (or the line above).
 * @return {{ IDDocument: number, Name: string }|null}
 */
const exampleCue = (pText) =>
{
	for (let i = 0; i < EXAMPLE_ID_PATTERNS.length; i++)
	{
		const tmpMatch = EXAMPLE_ID_PATTERNS[i].exec(pText);
		if (!tmpMatch)
		{
			continue;
		}
		const tmpEnd = tmpMatch.index + tmpMatch[0].length;
		let tmpName = pText.slice(tmpEnd).replace(/^[\s,:;*()[\]–—-]+/, '');
		const tmpStops = [ ':', '**', 'http', '(', ')', ' - ', ' — ', ' – ', '. ' ];
		tmpStops.forEach((pStop) =>
		{
			const tmpAt = tmpName.indexOf(pStop);
			if (tmpAt >= 0)
			{
				tmpName = tmpName.slice(0, tmpAt);
			}
		});
		return { IDDocument: Number(tmpMatch[1]) || 0, Name: cleanLine(tmpName.replace(/[\s*,;]+$/, ''), 80) };
	}
	return null;
};

/**
 * Best-effort example documents in assistant text: every render URL, with the document id and label written
 * nearest before it (the node replies like '**4913644, Single Award:** … View: https://…/1.0/Report/<guid>/index.html').
 * @param {string} pContent - Assistant Content.
 * @return {Array<{ IDDocument: number, Name: string, RenderURL: string }>}
 */
const examplesFromContent = (pContent) =>
{
	/** @type {Array<{ IDDocument: number, Name: string, RenderURL: string }>} */
	const tmpFound = [];
	if (typeof pContent !== 'string')
	{
		return tmpFound;
	}
	const tmpURLPattern = /https:\/\/[^\s<>()"'[\]*`]+/g;
	let tmpMatch = tmpURLPattern.exec(pContent);
	while (tmpMatch && (tmpFound.length < EXAMPLES_MAX))
	{
		const tmpURL = reportURL(tmpMatch[0].replace(/[.,;:!?]+$/, ''));
		if (tmpURL)
		{
			const tmpLineStart = pContent.lastIndexOf('\n', tmpMatch.index) + 1;
			let tmpCue = exampleCue(pContent.slice(tmpLineStart, tmpMatch.index));
			if (!tmpCue && (tmpLineStart > 1))
			{
				const tmpAbove = pContent.slice(0, tmpLineStart - 1);
				const tmpAboveLine = tmpAbove.slice(tmpAbove.lastIndexOf('\n') + 1);
				tmpCue = exampleCue(tmpAboveLine);
			}
			tmpFound.push({ IDDocument: tmpCue ? tmpCue.IDDocument : 0, Name: tmpCue ? tmpCue.Name : '', RenderURL: tmpURL });
		}
		tmpMatch = tmpURLPattern.exec(pContent);
	}
	return tmpFound;
};

/**
 * Normalize a Files list for FormData.
 * @param {any} pFiles
 * @return {Array<{ Name: string, MimeType: string, Size: number, IDMedia: number, Converted: any, Role: string }>}
 */
const normalizeFiles = (pFiles) =>
{
	if (!Array.isArray(pFiles))
	{
		return [];
	}
	return pFiles.filter(isPlainObject).slice(0, 200).map((pFile) =>
	{
		return {
			Name: cleanLine(pFile.Name, 256),
			MimeType: cleanLine(pFile.MimeType, 96),
			Size: Number(pFile.Size) || 0,
			IDMedia: Number(pFile.IDMedia) || 0,
			Converted: (typeof pFile.Converted === 'string') ? cleanLine(pFile.Converted, 32) : !!pFile.Converted,
			Role: cleanLine(pFile.Role, 32),
		};
	});
};

/**
 * @param {{ IDMedia: number, Name: string }} pFile
 * @return {string}
 */
const fileKey = (pFile) => ((pFile.IDMedia > 0) ? `m${pFile.IDMedia}` : `n${pFile.Name}`);

/**
 * Union of two file lists (by IDMedia, else Name); entries in pNewer win.
 * @param {Array<any>} pOlder
 * @param {Array<any>} pNewer
 * @return {Array<any>}
 */
const mergeFiles = (pOlder, pNewer) =>
{
	/** @type {Array<any>} */
	const tmpMerged = [];
	/** @type {Record<string, number>} */
	const tmpIndex = {};
	(pOlder || []).concat(pNewer || []).forEach((pFile) =>
	{
		const tmpKey = fileKey(pFile);
		if (tmpIndex[tmpKey] === undefined)
		{
			tmpIndex[tmpKey] = tmpMerged.length;
			tmpMerged.push(pFile);
		}
		else
		{
			tmpMerged[tmpIndex[tmpKey]] = pFile;
		}
	});
	return tmpMerged.slice(0, 200);
};

/**
 * A Document read's FormData as an object ({} when absent or unparseable).
 * @param {any} pDocument
 * @return {Record<string, any>}
 */
const documentFormData = (pDocument) =>
{
	if (!pDocument)
	{
		return {};
	}
	let tmpFormData = (pDocument.FormData !== undefined) ? pDocument.FormData : pDocument.FormDataJSON;
	if (typeof tmpFormData === 'string')
	{
		try
		{
			tmpFormData = JSON.parse(tmpFormData);
		}
		catch (pError)
		{
			tmpFormData = {};
		}
	}
	return isPlainObject(tmpFormData) ? tmpFormData : {};
};

/**
 * The mirrored status recorded in a mirror Document's Description (written by this module), or ''.
 * @param {any} pDescription
 * @return {string}
 */
const recordedStatus = (pDescription) =>
{
	const tmpMatch = (typeof pDescription === 'string') ? /Status: ([A-Za-z]{1,32})\s*$/.exec(pDescription) : null;
	return tmpMatch ? tmpMatch[1] : '';
};

/**
 * @param {any} pError
 * @return {string}
 */
const errorMessage = (pError) => cleanLine((pError && pError.message) ? pError.message : pError, 300);

/**
 * Meadow's create refusal for a GUID already held (live or soft-deleted).
 * @param {any} pError
 * @return {boolean}
 */
const isDuplicateGUIDError = (pError) => /already exists/i.test(errorMessage(pError));

/**
 * The Headlight-side mirror of Form Concierge sessions (one instance per screen; it caches the form type, the
 * sessions project and each session's resolved Document).
 */
class FormConciergeMirror
{
	/**
	 * @param {{ rest: any, IDCustomer: number, Environment?: string, log?: any, now?: () => number }} pOptions -
	 *   rest = HeadlightRestClient-like (getJSON/postJSON/putJSON with (err, body) callbacks, URLs without /1.0/);
	 *   now = clock for tests.
	 */
	constructor(pOptions)
	{
		/** @type {Record<string, any>} */
		const tmpOptions = pOptions || {};
		/** @type {any} */
		this.rest = tmpOptions.rest || null;
		/** @type {number} */
		this.IDCustomer = Number(tmpOptions.IDCustomer) || 0;
		/** @type {string} */
		this.Environment = cleanLine(tmpOptions.Environment, 16);
		/** @type {any} */
		this.log = tmpOptions.log || null;
		/** @type {() => number} */
		this._now = (typeof tmpOptions.now === 'function') ? tmpOptions.now : () => Date.now();
		/** @type {{ Result: any, Transient: boolean, At: number }|null} */
		this._ready = null;
		/** @type {Promise<any>|null} */
		this._readyPromise = null;
		/** @type {{ IDProject: number, GUIDProject: string }|null} */
		this._project = null;
		/** @type {Promise<any>|null} */
		this._projectPromise = null;
		/** @type {string} */
		this._formLinkKey = '';
		// Per-session mirror state (provider-lifetime cache): GUIDRequest → { IDDocument, GUIDDocument, IDProject, Key, Status, Files, FilesLoaded }.
		/** @type {Record<string, any>} */
		this._docs = {};
		/** @type {Record<string, Promise<any>>} */
		this._locks = {};
		/** @type {Promise<any>|null} */
		this._reconcilePromise = null;
		/** @type {number} */
		this._lastStamp = 0;
	}

	/**
	 * 'FormConcierge-Session-' + GUIDRequest.
	 * @param {string} pGUIDRequest - The plansheet Request GUID.
	 * @return {string}
	 */
	static documentGUID(pGUIDRequest)
	{
		return DOCUMENT_GUID_PREFIX + String(pGUIDRequest || '');
	}

	/**
	 * The change signature of a session: (Request.Status, Messages.length, last message Status, last message
	 * IDRequestMessage), plus the zombie status when the façade flagged the active run as never picked up.
	 * @param {any} pPayload - GET Session payload.
	 * @return {string}
	 */
	static signature(pPayload)
	{
		const tmpSession = (pPayload && isPlainObject(pPayload.Session)) ? pPayload.Session : {};
		const tmpMessages = sortedMessages(pPayload);
		const tmpLast = tmpMessages.length ? tmpMessages[tmpMessages.length - 1] : null;
		const tmpStatus = cleanLine(tmpSession.Status, 32);
		const tmpParts = [ tmpStatus, String(tmpMessages.length), tmpLast ? cleanLine(tmpLast.Status, 32) : '', String(tmpLast ? (Number(tmpLast.IDRequestMessage) || 0) : 0) ];
		const tmpEffective = effectiveStatus(pPayload);
		if (tmpEffective !== tmpStatus)
		{
			tmpParts.push(tmpEffective);
		}
		return tmpParts.join('|');
	}

	/**
	 * The mirror Document's FormData (§5 shape). User turns carry only what the user typed (the façade's
	 * Display.UserText — never the assembled prompt or steering); Metadata is always an object or null.
	 * @param {any} pPayload - GET Session payload.
	 * @param {{ Files?: Array<any>, Environment?: string, MirroredAt?: string, TaskName?: string }} [pExtras]
	 * @return {Record<string, any>}
	 */
	static buildFormData(pPayload, pExtras)
	{
		return neutralizeMarkup(FormConciergeMirror._buildFormDataRaw(pPayload, pExtras));
	}

	/**
	 * buildFormData before markup neutralization.
	 * @param {any} pPayload - GET Session payload.
	 * @param {{ Files?: Array<any>, Environment?: string, MirroredAt?: string, TaskName?: string }} [pExtras]
	 * @return {Record<string, any>}
	 */
	static _buildFormDataRaw(pPayload, pExtras)
	{
		const tmpExtras = pExtras || {};
		const tmpSession = (pPayload && isPlainObject(pPayload.Session)) ? pPayload.Session : {};
		const tmpForm = (pPayload && isPlainObject(pPayload.Form)) ? pPayload.Form : {};
		const tmpMessages = sortedMessages(pPayload);
		const tmpFlow = cleanLine(tmpSession.Flow, 32).toLowerCase();
		const tmpIDRequest = Number(tmpSession.IDRequest) || 0;
		const tmpFormName = cleanLine(tmpSession.FormName || tmpForm.Name, 200);
		let tmpTitle = cleanLine(tmpSession.Name, 200);
		if (!tmpTitle)
		{
			tmpTitle = FLOW_LABELS[tmpFlow] ? `${FLOW_LABELS[tmpFlow]}: ${tmpFormName || 'form'}` : (tmpFormName || `Session ${tmpIDRequest}`);
		}

		let tmpSeenUser = false;
		const tmpTurns = tmpMessages.map((pMessage) =>
		{
			const tmpRole = (cleanLine(pMessage.Role, 16).toLowerCase() === 'assistant') ? 'assistant' : 'user';
			let tmpContent = '';
			if (tmpRole === 'assistant')
			{
				tmpContent = (typeof pMessage.Content === 'string') ? pMessage.Content : '';
			}
			else if (isPlainObject(pMessage.Display) && (typeof pMessage.Display.UserText === 'string'))
			{
				tmpContent = pMessage.Display.UserText;
			}
			else
			{
				// No Display from the façade: never mirror the raw prompt — cut the standing instructions and steering.
				const tmpRaw = (typeof pMessage.Content === 'string') ? pMessage.Content : '';
				tmpContent = stripSteering(tmpSeenUser ? tmpRaw : userSegmentOfAssembledTurn(tmpRaw));
			}
			if (tmpRole === 'user')
			{
				tmpSeenUser = true;
			}
			return {
				Sequence: Number(pMessage.Sequence) || 0,
				Role: tmpRole,
				Status: cleanLine(pMessage.Status, 32),
				At: cleanLine((tmpRole === 'assistant') ? (pMessage.AnsweredDate || pMessage.CreateDate) : pMessage.CreateDate, 40),
				Content: cleanText(tmpContent, TURN_CONTENT_MAX),
				IDRun: Number(pMessage.IDRun) || 0,
				IDRequestMessage: Number(pMessage.IDRequestMessage) || 0,
				Metadata: parseMetadata((pMessage.Metadata !== undefined) ? pMessage.Metadata : pMessage.MetadataJSON),
			};
		});

		const tmpFirstUser = tmpTurns.filter((pTurn) => pTurn.Role === 'user')[0];
		const tmpTranscript = tmpTurns.map((pTurn) =>
		{
			const tmpRoleLabel = (pTurn.Role === 'assistant') ? 'Assistant' : 'User';
			return `### ${tmpRoleLabel} · ${transcriptDate(pTurn.At)} · ${pTurn.Status || '-'}\n\n${pTurn.Content || '(no text)'}`;
		}).join('\n\n');

		return {
			Session:
			{
				Title: tmpTitle,
				Flow: tmpFlow,
				Status: effectiveStatus(pPayload),
				FormName: tmpFormName,
				Environment: cleanLine(tmpExtras.Environment, 16),
				IDRequest: tmpIDRequest,
				GUIDRequest: cleanLine(tmpSession.GUIDRequest, 64),
				RequestNumber: Number(tmpSession.RequestNumber) || 0,
				PlansheetIDProject: Number(tmpSession.IDProject || tmpForm.IDProject) || 0,
				PlansheetGUIDProject: cleanLine(tmpSession.GUIDProject || tmpForm.GUIDProject, 64),
				IDTask: Number(tmpSession.IDTask) || 0,
				TaskName: cleanLine(tmpSession.TaskName || tmpExtras.TaskName, 200),
				CreatedAt: cleanLine(tmpSession.CreateDate, 40),
				AnsweredAt: cleanLine(tmpSession.AnsweredDate, 40),
				MirroredAt: (typeof tmpExtras.MirroredAt === 'string' && tmpExtras.MirroredAt) ? tmpExtras.MirroredAt : new Date().toISOString(),
				Signature: FormConciergeMirror.signature(pPayload),
			},
			Request: { Text: tmpFirstUser ? tmpFirstUser.Content : '' },
			Transcript: cleanText(tmpTranscript, TRANSCRIPT_MAX),
			Turns: tmpTurns,
			Files: normalizeFiles(tmpExtras.Files),
			Outputs: FormConciergeMirror._outputs(tmpTurns),
		};
	}

	/**
	 * Outputs from the latest assistant metadata carrying each key (Blueprint, HeadlightForm, Config), plus
	 * example documents from metadata arrays and render URLs in assistant Content (newest first, de-duplicated).
	 * @param {Array<any>} pTurns - FormData Turns.
	 * @return {Record<string, any>}
	 */
	static _outputs(pTurns)
	{
		let tmpBlueprint = null;
		let tmpHeadlightForm = null;
		let tmpConfig = null;
		/** @type {Array<{ IDDocument: number, Name: string, RenderURL: string }>} */
		const tmpExamples = [];
		/** @type {Record<string, boolean>} */
		const tmpSeen = {};
		/** @param {{ IDDocument: number, Name: string, RenderURL: string }} pExample */
		const fAdd = (pExample) =>
		{
			if (!pExample.RenderURL || tmpSeen[pExample.RenderURL] || (tmpExamples.length >= EXAMPLES_MAX))
			{
				return;
			}
			tmpSeen[pExample.RenderURL] = true;
			tmpExamples.push(pExample);
		};

		for (let i = pTurns.length - 1; i >= 0; i--)
		{
			const tmpTurn = pTurns[i];
			if (tmpTurn.Role !== 'assistant')
			{
				continue;
			}
			const tmpMeta = tmpTurn.Metadata;
			if (tmpMeta)
			{
				if (!tmpBlueprint && isPlainObject(tmpMeta.Blueprint))
				{
					tmpBlueprint = tmpMeta.Blueprint;
				}
				if (!tmpHeadlightForm && isPlainObject(tmpMeta.HeadlightForm))
				{
					tmpHeadlightForm = tmpMeta.HeadlightForm;
				}
				if (!tmpConfig && isPlainObject(tmpMeta.Config))
				{
					tmpConfig = tmpMeta.Config;
				}
				[ tmpMeta.Examples, tmpMeta.Documents ].forEach((pList) =>
				{
					if (!Array.isArray(pList))
					{
						return;
					}
					pList.filter(isPlainObject).forEach((pItem) =>
					{
						fAdd({ IDDocument: Number(pItem.IDDocument) || 0, Name: cleanLine(pItem.Name, 80), RenderURL: reportURL(pItem.RenderURL) });
					});
				});
			}
			examplesFromContent(tmpTurn.Content).forEach(fAdd);
		}

		const tmpShareURL = (tmpBlueprint && (typeof tmpBlueprint.ShareURL === 'string') && (tmpBlueprint.ShareURL.indexOf('https://plansheet.io/') === 0)) ? cleanLine(tmpBlueprint.ShareURL, 500) : '';
		return {
			IDBlueprint: Number(tmpBlueprint && tmpBlueprint.IDBlueprint) || 0,
			BlueprintShareURL: tmpShareURL,
			FormHash: cleanLine((tmpConfig && tmpConfig.FormHash) || (tmpHeadlightForm && tmpHeadlightForm.Hash), 128),
			Pushed: !!(tmpHeadlightForm && (tmpHeadlightForm.Pushed === true)),
			DeliveryEnvironment: cleanLine(tmpHeadlightForm && tmpHeadlightForm.Environment, 100),
			DeliveryCustomer: cleanLine(tmpHeadlightForm && tmpHeadlightForm.Customer, 300),
			Command: cleanLine(tmpHeadlightForm && tmpHeadlightForm.Command, 2000),
			Examples: tmpExamples,
		};
	}

	// ── REST plumbing ───────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Promise wrapper over the injected rest client. A body carrying Error/ErrorCode is a failure too.
	 * @param {string} pMethod - 'getJSON' | 'postJSON' | 'putJSON'.
	 * @param {string} pURL - Without the /1.0/ prefix.
	 * @param {any} [pBody] - JSON body (post/put).
	 * @return {Promise<any>}
	 */
	_call(pMethod, pURL, pBody)
	{
		return new Promise((fResolve, fReject) =>
		{
			if (!this.rest || (typeof this.rest[pMethod] !== 'function'))
			{
				return fReject(new Error('no Headlight REST client'));
			}
			/**
			 * @param {any} pError
			 * @param {any} pResult
			 */
			const fDone = (pError, pResult) =>
			{
				if (pError)
				{
					return fReject(pError);
				}
				if (pResult && (typeof pResult === 'object') && !Array.isArray(pResult) && (pResult.Error || pResult.ErrorCode))
				{
					return fReject(new Error(String(pResult.Error || `ErrorCode ${pResult.ErrorCode}`)));
				}
				return fResolve(pResult);
			};
			try
			{
				if (pMethod === 'getJSON')
				{
					this.rest.getJSON(pURL, fDone);
				}
				else
				{
					this.rest[pMethod](pURL, pBody, fDone);
				}
			}
			catch (pError)
			{
				fReject(pError);
			}
		});
	}

	/**
	 * @param {string} pURL
	 * @return {Promise<any>}
	 */
	_get(pURL)
	{
		return this._call('getJSON', pURL);
	}

	/**
	 * @param {string} pURL
	 * @param {any} pBody
	 * @return {Promise<any>}
	 */
	_post(pURL, pBody)
	{
		return this._call('postJSON', pURL, pBody);
	}

	/**
	 * @param {string} pURL
	 * @param {any} pBody
	 * @return {Promise<any>}
	 */
	_put(pURL, pBody)
	{
		return this._call('putJSON', pURL, pBody);
	}

	/**
	 * @param {string} pMessage
	 */
	_warn(pMessage)
	{
		if (this.log && (typeof this.log.warn === 'function'))
		{
			try
			{
				this.log.warn(`FormConcierge mirror: ${pMessage}`);
			}
			catch (pError)
			{
				// logging is best-effort
			}
		}
	}

	/**
	 * Run pWork after any earlier work on the same key (one sync per session at a time).
	 * @param {string} pKey
	 * @param {() => Promise<any>} pWork
	 * @return {Promise<any>}
	 */
	_withLock(pKey, pWork)
	{
		const tmpPrevious = this._locks[pKey] || Promise.resolve();
		const tmpRun = tmpPrevious.then(pWork, pWork);
		const tmpTail = tmpRun.then(() => null, () => null);
		this._locks[pKey] = tmpTail;
		tmpTail.then(() =>
		{
			if (this._locks[pKey] === tmpTail)
			{
				delete this._locks[pKey];
			}
		});
		return tmpRun;
	}

	/**
	 * A strictly increasing ISO stamp (two writes in one millisecond still verify distinctly).
	 * @return {string}
	 */
	_stamp()
	{
		let tmpNow = Number(this._now()) || Date.now();
		if (tmpNow <= this._lastStamp)
		{
			tmpNow = this._lastStamp + 1;
		}
		this._lastStamp = tmpNow;
		return new Date(tmpNow).toISOString();
	}

	// ── Readiness (form type) ───────────────────────────────────────────────────────────────────────────────

	/**
	 * Is the FormConcierge-Session form type installed for this customer (or globally)? Cached: a found or missing
	 * form type for the life of this mirror, a failed lookup for a minute.
	 * @return {Promise<{ Ready: boolean, IDReportNamedInstance?: number, Reason?: string }>}
	 */
	async checkReady()
	{
		try
		{
			const tmpCached = this._ready;
			if (tmpCached && (!tmpCached.Transient || ((this._now() - tmpCached.At) < READY_RETRY_MS)))
			{
				return Object.assign({}, tmpCached.Result);
			}
			if (!this._readyPromise)
			{
				this._readyPromise = this._lookupFormType().then((pReady) =>
				{
					this._ready = pReady;
					this._readyPromise = null;
					return pReady;
				}, (pError) =>
				{
					this._readyPromise = null;
					throw pError;
				});
			}
			const tmpReady = await this._readyPromise;
			return Object.assign({}, tmpReady.Result);
		}
		catch (pError)
		{
			return { Ready: false, Reason: `Not mirrored to Headlight: ${errorMessage(pError)}` };
		}
	}

	/**
	 * One lean RNI read (§5). The customer's own RNI wins over a global (customer 0) one.
	 * @return {Promise<{ Result: any, Transient: boolean, At: number }>}
	 */
	async _lookupFormType()
	{
		const tmpAt = this._now();
		const tmpCustomer = this.IDCustomer;
		if (!(tmpCustomer > 0) || (Math.floor(tmpCustomer) !== tmpCustomer))
		{
			return { Result: { Ready: false, Reason: 'Not mirrored to Headlight: there is no session customer.' }, Transient: true, At: tmpAt };
		}
		let tmpRows;
		try
		{
			tmpRows = await this._get(`ReportNamedInstances/LiteExtended/Hash,Name/FilteredTo/FBV~Hash~EQ~${RNI_HASH}~FBL~IDCustomer~INN~${tmpCustomer},0~FBV~Deleted~EQ~0/0/2`);
		}
		catch (pError)
		{
			return { Result: { Ready: false, Reason: `Not mirrored to Headlight: could not check the FormConcierge-Session form type (${errorMessage(pError)}).` }, Transient: true, At: tmpAt };
		}
		if (!Array.isArray(tmpRows))
		{
			return { Result: { Ready: false, Reason: 'Not mirrored to Headlight: the form type lookup answered unexpectedly.' }, Transient: true, At: tmpAt };
		}
		const tmpCandidates = tmpRows.filter((pRow) => isPlainObject(pRow) && (Number(pRow.IDReportNamedInstance) > 0) && (!pRow.Hash || (pRow.Hash === RNI_HASH)));
		const tmpRNI = tmpCandidates.filter((pRow) => Number(pRow.IDCustomer) === tmpCustomer)[0] || tmpCandidates.filter((pRow) => Number(pRow.IDCustomer) === 0)[0] || null;
		if (!tmpRNI)
		{
			return { Result: { Ready: false, Reason: NOT_INSTALLED_REASON }, Transient: false, At: tmpAt };
		}
		return { Result: { Ready: true, IDReportNamedInstance: Number(tmpRNI.IDReportNamedInstance) }, Transient: false, At: tmpAt };
	}

	// ── Deterministic-GUID location ─────────────────────────────────────────────────────────────────────────

	/**
	 * Walk the GUID suffixes from pFromIndex: a live row → use it; a GUID held only by a deleted row → next suffix;
	 * otherwise this GUID is free. A live-probe failure throws (never guess and risk a duplicate).
	 * @param {string} pBase - The deterministic GUID.
	 * @param {number} pFromIndex - First suffix index to try.
	 * @param {(pGUID: string) => Promise<any>} fProbeLive
	 * @param {(pGUID: string) => Promise<any>} fProbeAny - Includes soft-deleted rows.
	 * @return {Promise<{ Index: number, GUID: string, Row: any }|null>} null when every suffix is held by a deleted row.
	 */
	async _locateGUID(pBase, pFromIndex, fProbeLive, fProbeAny)
	{
		for (let i = pFromIndex; i < GUID_SUFFIXES.length; i++)
		{
			const tmpGUID = pBase + GUID_SUFFIXES[i];
			const tmpLive = await fProbeLive(tmpGUID);
			if (tmpLive)
			{
				return { Index: i, GUID: tmpGUID, Row: tmpLive };
			}
			let tmpHeld = false;
			try
			{
				tmpHeld = !!(await fProbeAny(tmpGUID));
			}
			catch (pError)
			{
				// best-effort: the create's duplicate-GUID refusal is the backstop
				tmpHeld = false;
			}
			if (!tmpHeld)
			{
				return { Index: i, GUID: tmpGUID, Row: null };
			}
		}
		return null;
	}

	/**
	 * Probe Projects by GUID.
	 * @param {string} pGUID
	 * @param {boolean} pIncludeDeleted
	 * @return {Promise<any>} The matching row or null.
	 */
	async _probeProject(pGUID, pIncludeDeleted)
	{
		const tmpRows = await this._get(`Projects/FilteredTo/FBV~GUIDProject~EQ~${encodeURIComponent(pGUID)}/0/2${pIncludeDeleted ? '?includeDeleted=true' : ''}`);
		if (!Array.isArray(tmpRows))
		{
			throw new Error('the project lookup answered unexpectedly');
		}
		return tmpRows.filter((pRow) => isPlainObject(pRow) && (Number(pRow.IDProject) > 0) &&
			((pRow.GUIDProject === undefined) || (pRow.GUIDProject === pGUID)) &&
			(pIncludeDeleted || (Number(pRow.Deleted) !== 1)))[0] || null;
	}

	/**
	 * Probe Documents by GUID (lean LiteExtended read; never pulls FormData).
	 * @param {string} pGUID
	 * @param {boolean} pIncludeDeleted
	 * @return {Promise<any>} The matching row or null.
	 */
	async _probeDocument(pGUID, pIncludeDeleted)
	{
		const tmpRows = await this._get(`Documents/LiteExtended/Name,DocumentType/FilteredTo/FBV~GUIDDocument~EQ~${encodeURIComponent(pGUID)}/0/1${pIncludeDeleted ? '?includeDeleted=true' : ''}`);
		if (!Array.isArray(tmpRows))
		{
			throw new Error('the document lookup answered unexpectedly');
		}
		return tmpRows.filter((pRow) => isPlainObject(pRow) && (Number(pRow.IDDocument) > 0) &&
			((pRow.GUIDDocument === undefined) || (pRow.GUIDDocument === pGUID)) &&
			(pIncludeDeleted || (Number(pRow.Deleted) !== 1)))[0] || null;
	}

	// ── Project + form link ─────────────────────────────────────────────────────────────────────────────────

	/**
	 * The sessions project (find-or-create, cached; concurrent callers share one attempt).
	 * @return {Promise<{ IDProject: number, GUIDProject: string }>}
	 */
	_ensureProject()
	{
		if (this._project)
		{
			return Promise.resolve(this._project);
		}
		if (!this._projectPromise)
		{
			this._projectPromise = this._findOrCreateProject().then((pProject) =>
			{
				this._project = pProject;
				this._projectPromise = null;
				return pProject;
			}, (pError) =>
			{
				this._projectPromise = null;
				throw pError;
			});
		}
		return this._projectPromise;
	}

	/**
	 * D12 find-or-create for `FormConcierge-<IDCustomer>-Sessions`.
	 * @return {Promise<{ IDProject: number, GUIDProject: string }>}
	 */
	async _findOrCreateProject()
	{
		const tmpBase = `FormConcierge-${this.IDCustomer}-Sessions`;
		const fLive = (/** @type {string} */ pGUID) => this._probeProject(pGUID, false);
		const fAny = (/** @type {string} */ pGUID) => this._probeProject(pGUID, true);
		let tmpFrom = 0;
		while (tmpFrom < GUID_SUFFIXES.length)
		{
			const tmpSpot = await this._locateGUID(tmpBase, tmpFrom, fLive, fAny);
			if (!tmpSpot)
			{
				break;
			}
			if (tmpSpot.Row)
			{
				return { IDProject: Number(tmpSpot.Row.IDProject), GUIDProject: tmpSpot.GUID };
			}
			let tmpCreated = null;
			let tmpError = null;
			try
			{
				tmpCreated = await this._post('Project', { GUIDProject: tmpSpot.GUID, Name: PROJECT_NAME, Status: PROJECT_STATUS, Description: PROJECT_DESCRIPTION });
			}
			catch (pError)
			{
				tmpError = pError;
			}
			// Read back: a refused write can still answer 200.
			const tmpBack = await fLive(tmpSpot.GUID);
			if (tmpBack)
			{
				return { IDProject: Number(tmpBack.IDProject), GUIDProject: tmpSpot.GUID };
			}
			if (isDuplicateGUIDError(tmpError))
			{
				tmpFrom = tmpSpot.Index + 1;
				continue;
			}
			throw new Error(`Headlight refused to create the Form Concierge sessions project${tmpError ? `: ${errorMessage(tmpError)}` : (tmpCreated ? ' (the write did not stick)' : '')}`);
		}
		throw new Error('every Form Concierge sessions project GUID (base, -2, -3) is held by a deleted project');
	}

	/**
	 * Ensure the RNI ↔ project join (no Deleted column on this table, so no Deleted filter). Cached once verified.
	 * @param {number} pIDProject
	 * @param {number} pIDReportNamedInstance
	 * @return {Promise<boolean>}
	 */
	async _ensureFormLink(pIDProject, pIDReportNamedInstance)
	{
		const tmpKey = `${pIDProject}:${pIDReportNamedInstance}`;
		if (this._formLinkKey === tmpKey)
		{
			return true;
		}
		const tmpURL = `ReportNamedInstanceProjectJoins/FilteredTo/FBV~IDProject~EQ~${pIDProject}~FBV~IDReportNamedInstance~EQ~${pIDReportNamedInstance}/0/1`;
		const fProbe = async () =>
		{
			const tmpRows = await this._get(tmpURL);
			if (!Array.isArray(tmpRows))
			{
				throw new Error('the form link lookup answered unexpectedly');
			}
			return tmpRows.filter((pRow) => isPlainObject(pRow) &&
				((pRow.IDProject === undefined) || (Number(pRow.IDProject) === pIDProject)) &&
				((pRow.IDReportNamedInstance === undefined) || (Number(pRow.IDReportNamedInstance) === pIDReportNamedInstance))).length > 0;
		};
		if (await fProbe())
		{
			this._formLinkKey = tmpKey;
			return true;
		}
		let tmpError = null;
		try
		{
			await this._post('ReportNamedInstanceProjectJoin', { IDReportNamedInstance: pIDReportNamedInstance, IDProject: pIDProject });
		}
		catch (pError)
		{
			tmpError = pError;
		}
		if (await fProbe())
		{
			this._formLinkKey = tmpKey;
			return true;
		}
		throw new Error(`could not join the FormConcierge-Session form to the sessions project${tmpError ? `: ${errorMessage(tmpError)}` : ''}`);
	}

	// ── Session sync ────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Mirror one session (the GET Session payload) into its Headlight Document. Never rejects.
	 * @param {any} pPayload - GET Session response.
	 * @param {{ Files?: Array<{ Name: string, MimeType: string, Size: number, IDMedia: number, Converted: any, Role: string }>, TaskName?: string }} [pExtras]
	 * @return {Promise<{ IDDocument: number, Created: boolean, Skipped: boolean, Reason?: string, Unchanged?: boolean, Warnings?: Array<string> }>}
	 */
	async syncSession(pPayload, pExtras)
	{
		let tmpGUIDRequest = '';
		try
		{
			const tmpSession = (pPayload && isPlainObject(pPayload.Session)) ? pPayload.Session : null;
			if (!tmpSession)
			{
				return { IDDocument: 0, Created: false, Skipped: true, Reason: 'Not mirrored to Headlight: the session could not be read.' };
			}
			tmpGUIDRequest = String(tmpSession.GUIDRequest || '');
			if (!GUID_PATTERN.test(tmpGUIDRequest))
			{
				return { IDDocument: 0, Created: false, Skipped: true, Reason: 'Not mirrored to Headlight: the session has no usable GUIDRequest.' };
			}
			return await this._withLock(tmpGUIDRequest, () => this._syncLocked(pPayload, pExtras || {}, tmpGUIDRequest));
		}
		catch (pError)
		{
			const tmpRecord = tmpGUIDRequest ? this._docs[tmpGUIDRequest] : null;
			this._warn(errorMessage(pError));
			return { IDDocument: (tmpRecord && tmpRecord.IDDocument) || 0, Created: false, Skipped: true, Reason: `Not mirrored to Headlight: ${errorMessage(pError)}` };
		}
	}

	/**
	 * The sync body, serialized per session by syncSession.
	 * @param {any} pPayload
	 * @param {Record<string, any>} pExtras
	 * @param {string} pGUIDRequest
	 * @return {Promise<{ IDDocument: number, Created: boolean, Skipped: boolean, Reason?: string, Unchanged?: boolean, Warnings?: Array<string> }>}
	 */
	async _syncLocked(pPayload, pExtras, pGUIDRequest)
	{
		const tmpRecord = this._docs[pGUIDRequest] || (this._docs[pGUIDRequest] = { IDDocument: 0, GUIDDocument: '', IDProject: 0, Key: '', Status: '', Files: [], FilesLoaded: false });
		if (Array.isArray(pExtras.Files))
		{
			tmpRecord.Files = mergeFiles(tmpRecord.Files, normalizeFiles(pExtras.Files));
		}
		const tmpKey = `${FormConciergeMirror.signature(pPayload)}#${JSON.stringify(tmpRecord.Files)}`;
		if (tmpRecord.IDDocument && (tmpRecord.Key === tmpKey))
		{
			return { IDDocument: tmpRecord.IDDocument, Created: false, Skipped: false, Unchanged: true };
		}

		const tmpReady = await this.checkReady();
		if (!tmpReady.Ready)
		{
			return { IDDocument: tmpRecord.IDDocument || 0, Created: false, Skipped: true, Reason: tmpReady.Reason || NOT_INSTALLED_REASON };
		}
		const tmpProject = await this._ensureProject();
		/** @type {Array<string>} */
		const tmpWarnings = [];
		try
		{
			await this._ensureFormLink(tmpProject.IDProject, Number(tmpReady.IDReportNamedInstance));
		}
		catch (pError)
		{
			// The data still mirrors; it only renders with the project's first form until the join exists (retried next sync).
			tmpWarnings.push('FormLinkMissing');
			this._warn(errorMessage(pError));
		}

		const tmpWritten = await this._writeDocument(pPayload, pExtras, pGUIDRequest, tmpRecord, tmpProject);
		tmpRecord.Key = tmpKey;
		tmpRecord.Status = tmpWritten.Status;
		/** @type {{ IDDocument: number, Created: boolean, Skipped: boolean, Warnings?: Array<string> }} */
		const tmpResult = { IDDocument: tmpRecord.IDDocument, Created: tmpWritten.Created, Skipped: false };
		if (tmpWarnings.length)
		{
			tmpResult.Warnings = tmpWarnings;
		}
		return tmpResult;
	}

	/**
	 * Locate (or create) the session's Document and write FormData, verifying every write by reading it back.
	 * A cached document that vanished (deleted under us) is re-located once.
	 * @param {any} pPayload
	 * @param {Record<string, any>} pExtras
	 * @param {string} pGUIDRequest
	 * @param {Record<string, any>} pRecord - The per-session cache entry (updated in place).
	 * @param {{ IDProject: number }} pProject
	 * @return {Promise<{ Created: boolean, Status: string }>}
	 */
	async _writeDocument(pPayload, pExtras, pGUIDRequest, pRecord, pProject)
	{
		const tmpBase = FormConciergeMirror.documentGUID(pGUIDRequest);
		const fLive = (/** @type {string} */ pGUID) => this._probeDocument(pGUID, false);
		const fAny = (/** @type {string} */ pGUID) => this._probeDocument(pGUID, true);
		/** @param {string} pStamp */
		const fBuild = (pStamp) => FormConciergeMirror.buildFormData(pPayload, { Files: pRecord.Files, Environment: this.Environment, MirroredAt: pStamp, TaskName: pExtras.TaskName });

		for (let tmpPass = 0; tmpPass < 2; tmpPass++)
		{
			let tmpCreated = false;
			if (!pRecord.IDDocument)
			{
				let tmpFrom = 0;
				while (!pRecord.IDDocument)
				{
					const tmpSpot = await this._locateGUID(tmpBase, tmpFrom, fLive, fAny);
					if (!tmpSpot)
					{
						throw new Error('every mirror document GUID (base, -2, -3) is held by a deleted document');
					}
					if (tmpSpot.Row)
					{
						pRecord.IDDocument = Number(tmpSpot.Row.IDDocument);
						pRecord.GUIDDocument = tmpSpot.GUID;
						pRecord.IDProject = Number(tmpSpot.Row.IDProject) || pProject.IDProject;
						pRecord.FilesLoaded = false;
						break;
					}
					const tmpFormData = fBuild(this._stamp());
					let tmpCreateError = null;
					try
					{
						await this._post('Document', this._documentRecord(tmpFormData, { GUIDDocument: tmpSpot.GUID, IDProject: pProject.IDProject, DocumentDate: this._documentDate(pPayload) }));
					}
					catch (pError)
					{
						tmpCreateError = pError;
					}
					const tmpBack = await fLive(tmpSpot.GUID);
					if (tmpBack)
					{
						pRecord.IDDocument = Number(tmpBack.IDDocument);
						pRecord.GUIDDocument = tmpSpot.GUID;
						pRecord.IDProject = pProject.IDProject;
						pRecord.FilesLoaded = true;
						tmpCreated = true;
						const tmpCheck = await this._verifyDocument(pRecord.IDDocument, tmpSpot.GUID, tmpFormData.Session.MirroredAt);
						if (tmpCheck.OK)
						{
							return { Created: true, Status: tmpFormData.Session.Status };
						}
						// Created, but the FormData didn't stick: fall through to the update path below.
						break;
					}
					if (isDuplicateGUIDError(tmpCreateError))
					{
						tmpFrom = tmpSpot.Index + 1;
						continue;
					}
					throw new Error(`Headlight refused to create the mirror document${tmpCreateError ? `: ${errorMessage(tmpCreateError)}` : ' (the write did not stick)'}`);
				}
			}

			if (!pRecord.FilesLoaded)
			{
				// First write to an existing document in this session: keep the Files it already lists.
				try
				{
					const tmpExisting = await this._get(`Document/${pRecord.IDDocument}`);
					pRecord.Files = mergeFiles(normalizeFiles(documentFormData(tmpExisting).Files), pRecord.Files);
					pRecord.FilesLoaded = true;
				}
				catch (pError)
				{
					// verification below decides whether the document is still there
				}
			}
			const tmpFormData = fBuild(this._stamp());
			let tmpPutError = null;
			try
			{
				await this._put('Document', this._documentRecord(tmpFormData, { IDDocument: pRecord.IDDocument, IDProject: pRecord.IDProject || pProject.IDProject }));
			}
			catch (pError)
			{
				tmpPutError = pError;
			}
			const tmpCheck = await this._verifyDocument(pRecord.IDDocument, pRecord.GUIDDocument, tmpFormData.Session.MirroredAt);
			if (tmpCheck.OK)
			{
				return { Created: tmpCreated, Status: tmpFormData.Session.Status };
			}
			if (tmpCheck.Gone && (tmpPass === 0) && !tmpCreated)
			{
				pRecord.IDDocument = 0;
				pRecord.GUIDDocument = '';
				pRecord.FilesLoaded = false;
				continue;
			}
			throw new Error(tmpPutError ? `Headlight refused the mirror update: ${errorMessage(tmpPutError)}` : 'Headlight did not keep the mirror update (the read-back did not match)');
		}
		throw new Error('the mirror document could not be written');
	}

	/**
	 * The Document body for a POST/PUT. FormData goes as an OBJECT; Description records the mirrored status so
	 * reconcile can spot a stale mirror from a lean list read.
	 * @param {Record<string, any>} pFormData
	 * @param {Record<string, any>} pFields - GUIDDocument/IDProject/DocumentDate (create) or IDDocument/IDProject (update).
	 * @return {Record<string, any>}
	 */
	_documentRecord(pFormData, pFields)
	{
		const tmpSession = pFormData.Session;
		const tmpNumber = tmpSession.RequestNumber || tmpSession.IDRequest;
		let tmpName = tmpSession.Title;
		const tmpSuffix = tmpNumber ? ` (#${tmpNumber})` : '';
		if ((tmpName.length + tmpSuffix.length) > DOCUMENT_NAME_MAX)
		{
			tmpName = tmpName.slice(0, Math.max(0, DOCUMENT_NAME_MAX - tmpSuffix.length - 1)).trim() + '…';
		}
		return Object.assign({}, pFields,
			{
				Name: (tmpName + tmpSuffix).slice(0, DOCUMENT_NAME_MAX),
				DocumentType: DOCUMENT_TYPE,
				Form: 1,
				FormData: pFormData,
				Description: `Form Concierge session mirror: plansheet request ${tmpSession.IDRequest}. Status: ${tmpSession.Status || 'Unknown'}`,
			});
	}

	/**
	 * DocumentDate = the Request's CreateDate when it parses, else now.
	 * @param {any} pPayload
	 * @return {string}
	 */
	_documentDate(pPayload)
	{
		const tmpMillis = toMillis(pPayload && pPayload.Session && pPayload.Session.CreateDate);
		return new Date(tmpMillis || this._now()).toISOString();
	}

	/**
	 * Read the Document back and check it holds THIS write (GUID + FormData.Session.MirroredAt).
	 * @param {number} pIDDocument
	 * @param {string} pGUID
	 * @param {string} pStamp
	 * @return {Promise<{ OK: boolean, Gone: boolean }>}
	 */
	async _verifyDocument(pIDDocument, pGUID, pStamp)
	{
		let tmpDocument;
		try
		{
			tmpDocument = await this._get(`Document/${Number(pIDDocument)}`);
		}
		catch (pError)
		{
			return { OK: false, Gone: true };
		}
		if (!isPlainObject(tmpDocument) || (Number(tmpDocument.IDDocument) !== Number(pIDDocument)) || (Number(tmpDocument.Deleted) === 1) ||
			(pGUID && tmpDocument.GUIDDocument && (tmpDocument.GUIDDocument !== pGUID)))
		{
			return { OK: false, Gone: true };
		}
		const tmpSession = documentFormData(tmpDocument).Session;
		return { OK: !!(tmpSession && (tmpSession.MirroredAt === pStamp)), Gone: false };
	}

	// ── Reconcile ───────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Catch up mirrors for sessions that finished while nobody had them open (D12): ONE lean Documents read for the
	 * listed sessions, then at most 5 sequential fetch+sync for sessions whose document is missing or stale.
	 * Never rejects; a call while one is running shares it.
	 * @param {Array<any>} pSessions - GET Sessions rows.
	 * @param {(pIDRequest: number) => Promise<any>} pFetchSession - Resolves a GET Session payload.
	 * @return {Promise<{ Synced: number, Errors: number, Checked: number, Failures: Array<{ IDRequest: number, Reason: string }>, Reason?: string }>}
	 */
	reconcile(pSessions, pFetchSession)
	{
		if (this._reconcilePromise)
		{
			return this._reconcilePromise;
		}
		const tmpRun = this._reconcile(pSessions, pFetchSession).then((pResult) => pResult, (pError) =>
		{
			this._warn(errorMessage(pError));
			return { Synced: 0, Errors: 1, Checked: 0, Failures: [ { IDRequest: 0, Reason: errorMessage(pError) } ] };
		});
		this._reconcilePromise = tmpRun.then((pResult) =>
		{
			this._reconcilePromise = null;
			return pResult;
		});
		return this._reconcilePromise;
	}

	/**
	 * @param {Array<any>} pSessions
	 * @param {(pIDRequest: number) => Promise<any>} pFetchSession
	 * @return {Promise<{ Synced: number, Errors: number, Checked: number, Failures: Array<{ IDRequest: number, Reason: string }>, Reason?: string }>}
	 */
	async _reconcile(pSessions, pFetchSession)
	{
		/** @type {{ Synced: number, Errors: number, Checked: number, Failures: Array<{ IDRequest: number, Reason: string }>, Reason?: string }} */
		const tmpResult = { Synced: 0, Errors: 0, Checked: 0, Failures: [] };
		const tmpRows = (Array.isArray(pSessions) ? pSessions : [])
			.filter((pRow) => isPlainObject(pRow) && (Number(pRow.IDRequest) > 0) && GUID_PATTERN.test(String(pRow.GUIDRequest || '')))
			.slice(0, RECONCILE_PROBE_CAP);
		if (tmpRows.length < 1)
		{
			return tmpResult;
		}
		if (typeof pFetchSession !== 'function')
		{
			tmpResult.Reason = 'no session fetcher';
			return tmpResult;
		}
		const tmpReady = await this.checkReady();
		if (!tmpReady.Ready)
		{
			tmpResult.Reason = tmpReady.Reason;
			return tmpResult;
		}

		/** @param {any} pRow */
		const fGUIDFor = (pRow) =>
		{
			const tmpRecord = this._docs[String(pRow.GUIDRequest)];
			return (tmpRecord && tmpRecord.GUIDDocument) ? tmpRecord.GUIDDocument : FormConciergeMirror.documentGUID(pRow.GUIDRequest);
		};
		/** @type {Array<string>} */
		const tmpGUIDs = [];
		tmpRows.forEach((pRow) =>
		{
			const tmpGUID = fGUIDFor(pRow);
			if (tmpGUIDs.indexOf(tmpGUID) < 0)
			{
				tmpGUIDs.push(tmpGUID);
			}
		});
		let tmpDocuments;
		try
		{
			tmpDocuments = await this._get(`Documents/LiteExtended/Name,DocumentType,Description/FilteredTo/FBL~GUIDDocument~INN~${tmpGUIDs.map((pGUID) => encodeURIComponent(pGUID)).join(',')}~FBV~Deleted~EQ~0/0/${RECONCILE_PROBE_CAP}`);
		}
		catch (pError)
		{
			tmpResult.Errors = 1;
			tmpResult.Failures.push({ IDRequest: 0, Reason: errorMessage(pError) });
			return tmpResult;
		}
		if (!Array.isArray(tmpDocuments))
		{
			tmpResult.Errors = 1;
			tmpResult.Failures.push({ IDRequest: 0, Reason: 'the mirror lookup answered unexpectedly' });
			return tmpResult;
		}
		/** @type {Record<string, any>} */
		const tmpByGUID = {};
		tmpDocuments.forEach((pDocument) =>
		{
			if (isPlainObject(pDocument) && (typeof pDocument.GUIDDocument === 'string') && (Number(pDocument.IDDocument) > 0))
			{
				tmpByGUID[pDocument.GUIDDocument] = pDocument;
			}
		});

		tmpResult.Checked = tmpRows.length;
		/** @type {Array<any>} */
		const tmpStale = [];
		tmpRows.forEach((pRow) =>
		{
			if (tmpStale.length >= RECONCILE_SYNC_CAP)
			{
				return;
			}
			const tmpGUIDRequest = String(pRow.GUIDRequest);
			const tmpGUID = fGUIDFor(pRow);
			const tmpDocument = tmpByGUID[tmpGUID];
			const tmpRecord = this._docs[tmpGUIDRequest];
			const tmpStatus = cleanLine(pRow.Status, 32);
			if (!tmpDocument)
			{
				if (tmpRecord)
				{
					// The cached document is gone (deleted) — forget it so the sync re-locates.
					tmpRecord.IDDocument = 0;
					tmpRecord.GUIDDocument = '';
					tmpRecord.Key = '';
					tmpRecord.FilesLoaded = false;
				}
				tmpStale.push(pRow);
				return;
			}
			if (!tmpRecord)
			{
				// Seed the cache so the sync skips the GUID probes.
				this._docs[tmpGUIDRequest] = { IDDocument: Number(tmpDocument.IDDocument), GUIDDocument: tmpGUID, IDProject: Number(tmpDocument.IDProject) || 0, Key: '', Status: '', Files: [], FilesLoaded: false };
			}
			else if (tmpRecord.Key && (tmpRecord.Status === tmpStatus))
			{
				// Already mirrored at this status by this screen.
				return;
			}
			const tmpRecorded = recordedStatus(tmpDocument.Description);
			if (tmpRecorded && (tmpRecorded !== tmpStatus))
			{
				tmpStale.push(pRow);
				return;
			}
			const tmpAnswered = toMillis(pRow.AnsweredDate);
			const tmpUpdated = toMillis(tmpDocument.UpdateDate);
			if (tmpAnswered && tmpUpdated && (tmpAnswered > tmpUpdated))
			{
				tmpStale.push(pRow);
			}
		});

		for (let i = 0; i < tmpStale.length; i++)
		{
			const tmpIDRequest = Number(tmpStale[i].IDRequest);
			let tmpPayload;
			try
			{
				tmpPayload = await pFetchSession(tmpIDRequest);
			}
			catch (pError)
			{
				tmpResult.Errors++;
				tmpResult.Failures.push({ IDRequest: tmpIDRequest, Reason: errorMessage(pError) });
				continue;
			}
			const tmpSync = await this.syncSession(tmpPayload);
			if (tmpSync.Skipped)
			{
				tmpResult.Errors++;
				tmpResult.Failures.push({ IDRequest: tmpIDRequest, Reason: tmpSync.Reason || 'skipped' });
			}
			else
			{
				tmpResult.Synced++;
			}
		}
		return tmpResult;
	}
}

module.exports = { FormConciergeMirror };
